import { formatTargetSip, formatTargetSipLabel } from '../../../display-values/sipPlanAmount'
import { usePlanSipStartAction } from '../../../execution/sip/start/usePlanSipStartAction'
import { openRecommendationFundDetail } from '../../../lumpsum/plan-bottom-sheet/open/planRecommendationOpen'
import { PlanRecommendationFundTile } from '../../../plans-ui-components/PlanRecommendationFundTile'
import { SipRecommendationActionType } from '../../api/common.types'
import type { SipStartRecommendation } from '../../api/sipPlan.types'
import type { SipPlanRecommendationDetails } from '../api/sipPlanRecommendation.types'
import { SipPlanRecommendationSheetErrorState } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetErrorState'
import { SipPlanRecommendationSheetLayout } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetLayout'
import { SipPlanRecommendationSheetSkeleton } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetSkeleton'
import { SipStartPlanRecommendationBody } from './body/SipStartPlanRecommendationBody'
import { PlansFundRoleBySipAction, PlansScreen } from '../../../analytics/plansEvents'
import {
  trackPlansSipFundTileOpen,
  trackPlansSipFundTilePrimaryCta
} from '../../../analytics/plansTrack'
import { ALLOCATION_PLANS_THEME } from '../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'

type SipStartPlanRecommendationSheetProps = {
  onDismiss: () => void
  planDetails: SipPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  recommendation: SipStartRecommendation
  initiateSipCta?: unknown
}

export function SipStartPlanRecommendationSheet({
  onDismiss,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  recommendation,
  initiateSipCta
}: SipStartPlanRecommendationSheetProps) {
  const idealSip = planDetails?.idealSip ?? recommendation?.recommendedSipAmount ?? 0
  const idealLabel = `${formatTargetSipLabel({ rawAmount: idealSip })}/mo`
  const isin = recommendation?.isin ?? planDetails?.isin ?? ''
  const { onStartSip } = usePlanSipStartAction({ isRecommendationSheetSource: true })
  const formattedTargetSip = formatTargetSip({ rawAmount: idealSip })
  const screenName = PlansScreen.SipYourPlanRecommendation
  const startSipActionLabel = `Start SIP of ${idealLabel}`

  return (
    <SipPlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      analyticsScreen={PlansScreen.SipYourPlanRecommendation}
    >
      {isLoading ? (
        <SipPlanRecommendationSheetSkeleton />
      ) : isFetchFailed ? (
        <SipPlanRecommendationSheetErrorState onRetry={onRetry} analyticsScreen={PlansScreen.SipYourPlanRecommendation} />
      ) : (
        <>
          <div className="px-5">
            <div className="flex flex-col gap-2">
              <div className="text-[14px] font-[500] leading-[130%]" style={{ color: ALLOCATION_PLANS_THEME.headerTextColor }}>
                Allocation Recommendation
              </div>
              <div className="text-[20px] leading-[130%] font-[400] text-[#6A6E6E] max-w-[90%]">
                {'Start a new SIP of '}
                <span className="font-[500] text-[#232323]">{idealLabel}</span>
                {' in this fund'}
              </div>
            </div>
          </div>
          <div className="px-5 pt-9 pb-4">
            <PlanRecommendationFundTile
              mfLogoUrl={planDetails?.mfLogoUrl ?? ''}
              fundName={planDetails?.fundName ?? ''}
              fundNature={planDetails?.fundNature ?? ''}
              fundCategory={planDetails?.fundCategory ?? ''}
              powerRating={planDetails?.powerRating ?? ''}
              powerRank={planDetails?.powerRank ?? null}
              purchaseMode={planDetails?.purchaseMode ?? ''}
              fundIsin={isin}
              onCardClick={() => {
                trackPlansSipFundTileOpen({
                  screenName,
                  fundRole: PlansFundRoleBySipAction[SipRecommendationActionType.START],
                  fundName: planDetails?.fundName ?? '',
                  fundIsin: isin
                })
                openRecommendationFundDetail(isin)
              }}
              actionFooter={{
                actionLabel: startSipActionLabel,
                onActionClick: () => {
                  trackPlansSipFundTilePrimaryCta({
                    screenName,
                    buttonLabel: startSipActionLabel,
                    fundName: planDetails?.fundName ?? '',
                    fundIsin: isin
                  })
                  void onStartSip({
                    isin,
                    fundName: planDetails?.fundName ?? '',
                    initiateSipCta,
                    amount: formattedTargetSip.value
                  })
                }
              }}
            />
          </div>
          <SipStartPlanRecommendationBody planDetails={planDetails} />
        </>
      )}
    </SipPlanRecommendationSheetLayout>
  )
}
